import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import candidateService from "../../../api/services/candidateService";
import jobService from "../../../api/services/jobService";
import departmentRoleService from "../../../api/services/departmentRoleService";
import {
  validateField,
  VALIDATION_RULES,
  ErrorMessage,
  INVALID_CHARS_REGEX,
  VALID_TEXT_REGEX
} from "../../../utils/formValidation";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Save,
  XCircle,
  User,
  GraduationCap,
  Briefcase,
  FileText,
  Star,
  Loader2,
  X,
  Upload,
  CheckCircle,
  Plus,
  Eye,
} from "lucide-react";
import { Country, State, City } from "country-state-city";
import LocationSelect from "../../../components/common/LocationSelect/LocationSelect";
import { post, del } from "../../../api/apiClient";
import { API_ENDPOINTS, API_BASE_URL } from "../../../api/config";
import { skillService } from "../../../api/crm/config/configService";
import Button from "../../../components/common/Button/Button";

const AddCandidate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get ID from URL for edit mode
  const { user } = useAuth();
  const [errors, setErrors] = useState({});

  const [saving, setSaving] = useState(false);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [isDeletingResume, setIsDeletingResume] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);

  // Location dropdown states - store full objects, not IDs
  const [country, setCountry] = useState(null);
  const [state, setState] = useState(null);
  const [city, setCity] = useState(null);

  // Location dropdown options
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  // Candidate data for editing
  const [candidate, setCandidate] = useState(null);

  // Auto-fill state to prevent handlers from clearing during initial population
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  const candidateValidationRules = {
    firstName: { required: true, minLength: 2 },
    lastName: { required: false, minLength: 2 },
    email: { required: true, email: true },
    phone: { required: true, phone: true },
    job: { required: true, customLabel: "Job position" },
    department: { required: true },
    experience: { required: true },
    education: { required: true },
    university: { required: true },
    // graduationYear rule removed to handle custom date logic in validateEducationField
    streetAddress: { required: false, minLength: 5 },
    postalCode: { required: false, minLength: 3 },
    country: { required: false },
    state: { required: false },
    city: { required: false },
  };

  const [formData, setFormData] = useState({
    // Basic Information
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    status: "active",
    stage: "applied",

    // Address Information - nested structure
    address: {
      streetAddress: "",
      postalCode: "",
      country: null,
      state: null,
      city: null,
    },

    // Professional Information
    job: "", // Job ObjectId
    jobTitle: "",
    department: "",
    experience: "",
    experienceRef: "",
    currentCompany: "",
    expectedSalary: "",
    noticePeriod: "",

    // Education
    education: "",
    university: "",
    graduationYear: "",
    educations: [{ education: "", university: "", graduationYear: "" }],

    // Skills
    skills: [],
    // Additional Information
    linkedin: "",
    github: "",
    portfolio: "",
    source: "manual",
    notes: "",
    availability: "Available",
    rating: 0,

    // Resume Upload
    resume: null,
  });

  const [newSkill, setNewSkill] = useState("");
  // const [newTag, setNewTag] = useState(""); // Commented out - tags removed
  const [jobs, setJobs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [experienceLevels, setExperienceLevels] = useState([]);
  const [loadingExperiences, setLoadingExperiences] = useState(false);

  const stageOptions = [
    "applied",
    "screening",
    "interview",
    "offer",
    "hired",
    "rejected",
  ];


  const [sourceOptions, setSourceOptions] = useState([
  ]);

  const [commonSkills, setCommonSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);

  // Direct API call for departments to handle CORS issues
  const fetchDepartmentsDirectly = async () => {
    try {

      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_BASE_URL}/hr/departments?isActive=true&paginated=false&limit=1000`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  };

  // Load all countries on component mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    // Transform to match our LocationSelect format
    const formattedCountries = allCountries.map((c) => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode,
      ...c,
    }));
    setCountryOptions(formattedCountries);

  }, []);

  // Update state options when country changes
  useEffect(() => {
    if (country) {
      const states = State.getStatesOfCountry(country.isoCode);
      const formattedStates = states.map((s) => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s,
      }));
      setStateOptions(formattedStates);

    } else {
      setStateOptions([]);
      setState(null);
    }
  }, [country]);

  // Update city options when state changes
  useEffect(() => {
    if (country && state) {
      const cities = City.getCitiesOfState(country.isoCode, state.isoCode);
      const formattedCities = cities.map((c) => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c,
      }));
      setCityOptions(formattedCities);

    } else {
      setCityOptions([]);
      setCity(null);
    }
  }, [country, state]);

  // Load jobs on component mount
  useEffect(() => {
    const loadJobs = async () => {
      // If editing, wait for candidate data to be loaded so we know which job to include
      if (id && !candidate) return;

      try {


        const params = {
          page: 1,
          limit: 1000,
          status: "active,published", // Always want active/published jobs
        };

        // If editing, include the candidate's currently assigned job even if it's not active
        if (id && candidate) {
          // Normalize matching ID. Check for populated object, string ID, or friendly jobId code.
          const currentJobId =
            candidate.job?._id ||
            candidate.job?.id ||
            (typeof candidate.job === "string" ? candidate.job : null) ||
            candidate.jobId;

          if (currentJobId) {
            params.includeId = currentJobId;

          }
        }

        const result = await jobService.getJobs(params);



        if (result && (result.responseCode === 200 || result.success)) {
          const jobsData = result.data?.jobs || result.data || [];

          setJobs(jobsData);
        } else {

          setJobs([]);
        }
      } catch (err) {

        setJobs([]);
      }
    };

    loadJobs();
  }, [id, candidate]);

  // Load sources on component mount
  useEffect(() => {
    const loadSources = async () => {
      try {
        const result = await jobService.getPublicSources();
        if (result.success && Array.isArray(result.data)) {
          const fetchedSources = result.data.map(item => {
            if (typeof item === 'string') return { value: item, label: item };
            return {
              value: item.value || item._id || item.id || item.name,
              label: item.label || item.name || item.title || item.value
            };
          });

          setSourceOptions(fetchedSources);
        }
      } catch (err) {
        console.error("Failed to load sources:", err);
      }
    };

    loadSources();
  }, []);

  // Effect to map jobTitle and department, or sync from selected job
  useEffect(() => {
    // 1. Sync from selected Job Position if Department or Job Title is empty (Autofill)
    // This runs in BOTH Add and Edit modes to ensure consistent data
    if (formData.job && jobs.length > 0) {
      const selectedJob = jobs.find((j) => (j._id || j.id) === formData.job);
      if (selectedJob) {
        setFormData((prev) => {
          const updates = {};

          // Auto-fill department if empty or not matching system
          if (selectedJob.department) {
            const currentDept = prev.department ? prev.department.trim() : "";
            const targetDept = selectedJob.department.trim();

            // If empty OR doesn't match a known department, try to sync
            if (
              !currentDept ||
              !departments.some(
                (d) => d.toLowerCase() === currentDept.toLowerCase(),
              )
            ) {
              // Look for case-insensitive match in system list
              const matchingSystemDept = departments.find(
                (d) => d.toLowerCase() === targetDept.toLowerCase(),
              );

              // Use system one if found (for correct casing), otherwise use the job's one directly
              const deptToUse = matchingSystemDept || targetDept;

              if (deptToUse !== prev.department) {
                updates.department = deptToUse;
              }
            }
          }

          // Auto-fill job title if empty
          if (!prev.jobTitle && selectedJob.title) {
            updates.jobTitle = selectedJob.title;
          }

          return Object.keys(updates).length > 0
            ? { ...prev, ...updates }
            : prev;
        });
      }
    }

    // 2. Map jobTitle strings to a concrete job ID if missing (primarily for legacy/bulk data)
    if (id && formData.jobTitle && !formData.job && jobs.length > 0) {
      const normalizedTitle = formData.jobTitle.trim().toLowerCase();

      // Try exact match first (Title or JobID)
      let matchingJob = jobs.find(
        (job) =>
          job.title?.trim().toLowerCase() === normalizedTitle ||
          job.jobId?.trim().toLowerCase() === normalizedTitle,
      );

      // If no exact match, try partial match (Title starts with or contains)
      if (!matchingJob) {
        matchingJob = jobs.find(
          (job) =>
            job.title?.trim().toLowerCase().includes(normalizedTitle) ||
            normalizedTitle.includes(job.title?.trim().toLowerCase()),
        );
      }

      if (matchingJob) {
        const jobIdToUse = matchingJob._id || matchingJob.id;

        setFormData((prev) => ({ ...prev, job: jobIdToUse }));
      }
    }

    // 3. Map department string to match system dropdown exactly (for selection consistency)
    if (formData.department && departments.length > 0) {
      const normalizedDept = formData.department.trim().toLowerCase();

      // Try exact match first (case-insensitive)
      let matchingDept = departments.find(
        (d) => d.trim().toLowerCase() === normalizedDept,
      );

      // If no exact match, try a more lenient "contains" or "starts with" match
      if (!matchingDept) {
        matchingDept = departments.find((d) => {
          const systemDept = d.trim().toLowerCase();
          return (
            systemDept.includes(normalizedDept) ||
            normalizedDept.includes(systemDept)
          );
        });
      }

      // If we found a match and it's different (even just casing), update to system value
      if (matchingDept && matchingDept !== formData.department) {

        setFormData((prev) => ({ ...prev, department: matchingDept }));
      }
    }
  }, [id, formData.job, departments, jobs]); // Runs whenever job selection or system lists change

  // Effect to strictly enforce 'Engineering IT' for 'tester' role
  useEffect(() => {
    const checkTesterRole = () => {
      // Check jobTitle string
      const isTesterInTitle =
        formData.jobTitle?.trim().toLowerCase() === "tester";

      // Check selected job object title
      let isTesterInSelectedJob = false;
      if (formData.job && jobs.length > 0) {
        const selectedJob = jobs.find((j) => (j._id || j.id) === formData.job);
        if (selectedJob?.title?.trim().toLowerCase() === "tester") {
          isTesterInSelectedJob = true;
        }
      }

      if (isTesterInTitle || isTesterInSelectedJob) {
        // If it's a tester role, force Engineering IT
        const targetDept = "Engineering IT";

        // Find existing department that matches strictly or partially
        const matchedSystemDept = departments.find(
          (d) => d.trim().toLowerCase() === targetDept.toLowerCase(),
        );
        const deptToSet = matchedSystemDept || targetDept;

        if (formData.department !== deptToSet) {

          setFormData((prev) => ({ ...prev, department: deptToSet }));
        }
      }
    };

    checkTesterRole();
  }, [formData.job, formData.jobTitle, jobs, departments]); // Removed formData.department to allow manual overrides if needed

  // Load departments on component mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setLoadingDepartments(true);


        // Try the departments API first
        let result;
        try {
          result = await departmentRoleService.getDepartments({
            isActive: "true",
            paginated: "false",
            limit: 1000,
          });

        } catch (apiError) {


          // If CORS error or API fails, try direct API call
          if (
            apiError.message?.includes("CORS") ||
            apiError.message?.includes("Network Error") ||
            apiError.code === "ERR_NETWORK"
          ) {

            try {
              result = await fetchDepartmentsDirectly();

            } catch (directError) {
              setDepartments([]);
              return;
            }
          } else {
            throw apiError;
          }
        }

        // Handle different response formats
        let departmentsData = [];

        if (Array.isArray(result)) {
          // Direct array response
          departmentsData = result;

        } else if (result && result.data) {
          // Response with data property
          departmentsData = Array.isArray(result.data)
            ? result.data
            : result.data.departments || [];

        } else if (result && result.responseCode === 200) {
          // Standard API response format
          departmentsData = result.data?.departments || result.data || [];

        } else {

          departmentsData = [];
        }



        // Extract department names from the response
        const departmentNames = departmentsData
          .map((dept) => {
            if (typeof dept === "string") {
              return dept;
            } else if (dept && typeof dept === "object") {
              const name =
                dept.name ||
                dept.departmentName ||
                dept.title ||
                dept.department ||
                dept._id ||
                "";

              return name;
            }
            return "";
          });

        setDepartments(departmentNames);
      } catch (err) {

        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    loadDepartments();
  }, []);

  // Load experience levels on component mount
  useEffect(() => {
    const loadExperiences = async () => {
      try {
        setLoadingExperiences(true);

        const response = await fetch(`${API_BASE_URL}/jobs/public/experience-levels`);
        if (!response.ok) {
          throw new Error("Failed to fetch experience levels");
        }
        const result = await response.json();

        if (result && (result.success || result.responseCode === 200)) {
          const experienceData = result.data || [];
          setExperienceLevels(experienceData);
        } else {
          setExperienceLevels([]);
        }
      } catch (err) {
        setExperienceLevels([]);
      } finally {
        setLoadingExperiences(false);
      }
    };

    loadExperiences();
  }, []);

  // Resolve experienceRef if experience name matches but ref is empty
  useEffect(() => {
    if (formData.experience && !formData.experienceRef && experienceLevels.length > 0) {
      const selectedExp = experienceLevels.find(
        (exp) => exp.name === formData.experience
      );
      if (selectedExp) {
        setFormData((prev) => ({
          ...prev,
          experienceRef: selectedExp._id || selectedExp.id,
        }));
      }
    }
  }, [formData.experience, formData.experienceRef, experienceLevels]);

  // Load skills on component mount
  useEffect(() => {
    const loadSkills = async () => {
      try {
        setLoadingSkills(true);

        const result = await skillService.getAll({ isActive: true });

        if (result && (result.success || result.responseCode === 200)) {
          const skillsData = result.data || [];

          setCommonSkills(skillsData.map((s) => s.name));
        } else {

          setCommonSkills([]);
        }
      } catch (err) {

        setCommonSkills([]);
      } finally {
        setLoadingSkills(false);
      }
    };

    loadSkills();
  }, []);

  // Load candidate data if in edit mode
  useEffect(() => {
    if (!id) return;

    const fetchCandidate = async () => {
      try {

        const result = await candidateService.getCandidateById(id);

        if (result && (result.responseCode === 200 || result.success)) {
          const data = result.data?.candidate || result.data || result;


          // Set candidate data for auto-fill logic
          setCandidate(data);
          setIsAutoFilling(true);

          // Helper to normalize values
          const val = (v) => v || "";

          // Construct educations array
          let loadedEducations = [];
          if (data.educations && data.educations.length > 0) {
            loadedEducations = data.educations;
          } else if (data.education || data.university || data.graduationYear) {
            loadedEducations = [
              {
                education: data.education || "",
                university: data.university || "",
                graduationYear: data.graduationYear
                  ? String(data.graduationYear)
                  : "",
              },
            ];
          } else {
            loadedEducations = [
              { education: "", university: "", graduationYear: "" },
            ];
          }

          setFormData((prev) => ({
            ...prev,
            firstName: val(data.firstName),
            lastName: val(data.lastName),
            email: val(data.email),
            phone: val(data.phone),
            // Handle nested address object
            address: {
              streetAddress: data.address?.streetAddress || "",
              postalCode: data.address?.postalCode || "",
              country: data.address?.country || null,
              state: data.address?.state || null,
              city: data.address?.city || null,
            },
            status:
              data.status || data.isActive !== false ? "active" : "inactive",
            stage: val(data.recruitmentStatus) || val(data.stage) || "applied",

            job:
              data.job?._id ||
              data.job?.id ||
              (typeof data.job === "string" ? data.job : "") ||
              "",
            jobTitle: val(data.jobTitle) || data.job?.title || "",
            department: (() => {
              const deptVal =
                typeof data.department === "object"
                  ? data.department?.name || ""
                  : val(data.department);
              return deptVal || data.job?.department || "";
            })(),

            experience:
              typeof data.experience === "object"
                ? val(data.experience?.name)
                : val(data.experience),
            experienceRef:
              typeof data.experience === "object"
                ? data.experience?._id ||
                data.experience?.id ||
                data.experience?.ref
                : "",
            currentCompany: val(data.currentCompany),
            expectedSalary: val(data.expectedSalary),
            noticePeriod: val(data.noticePeriod),

            // Populate first education item for compatibility if needed, though usually loop is used
            education: loadedEducations[0]?.education || "",
            university: loadedEducations[0]?.university || "",
            graduationYear: loadedEducations[0]?.graduationYear || "",
            educations: loadedEducations,

            skills: data.skills || [],

            linkedin: val(data.linkedin),
            github: val(data.github),
            portfolio: val(data.portfolio),
            source: val(data.source) || "manual",
            notes: val(data.notes),
            availability: val(data.availability) || "Available",
            rating: data.rating || 0,

            resume: (() => {
              // Strict checks for null/undefined/empty
              if (!data.resume) return null;

              if (typeof data.resume === "string") {
                // Handle string literals that might be stored
                if (
                  data.resume === "null" ||
                  data.resume === "undefined" ||
                  data.resume.trim() === ""
                )
                  return null;

                // Construct object from string URL
                const parts = data.resume.split("/");
                const filename = parts[parts.length - 1] || "Resume.pdf";
                return {
                  url: data.resume,
                  name: filename,
                  size: 0,
                  type: "application/pdf",
                  originalName: filename,
                };
              }

              // Handle object
              if (typeof data.resume === "object") {
                // Check if object is empty
                if (Object.keys(data.resume).length === 0) return null;

                // Must have at least a URL or name to be considered valid
                if (
                  !data.resume.url &&
                  !data.resume.secure_url &&
                  !data.resume.name
                )
                  return null;

                // Normal object - ensure url exists for view button
                if (!data.resume.url && data.resume.secure_url) {
                  return { ...data.resume, url: data.resume.secure_url };
                }
                return data.resume;
              }

              return null;
            })(),
          }));
        } else {
          toast.error(
            result?.responseMessage || "Failed to load candidate details",
          );
          navigate(location.state?.from || "/hrms/back-office?tab=candidateProfiles");
        }
      } catch (err) {

        toast.error("Failed to load candidate data");
        navigate(location.state?.from || "/hrms/back-office?tab=candidateProfiles");
      }
    };

    fetchCandidate();
  }, [id, navigate, location]);

  // Auto-fill location dropdowns when editing a candidate
  useEffect(() => {
    if (!candidate?.address || countryOptions.length === 0) return;

    // Find and set country from saved data
    if (candidate.address.country) {
      const savedCountry = candidate.address.country;
      // Try to find by iso2 or isoCode
      const matchedCountry = countryOptions.find(
        (c) =>
          c.isoCode === savedCountry.iso2 ||
          c.isoCode === savedCountry.isoCode ||
          c.name === savedCountry.name,
      );

      if (matchedCountry) {

        setCountry(matchedCountry);
      }
    }

    // Mark auto-fill as complete after a short delay
    setTimeout(() => {
      setIsAutoFilling(false);
    }, 500);
  }, [candidate, countryOptions]);

  // Auto-fill state after country options are loaded
  useEffect(() => {
    if (!candidate?.address?.state || stateOptions.length === 0) return;

    const savedState = candidate.address.state;
    const matchedState = stateOptions.find(
      (s) =>
        s.isoCode === savedState.state_code ||
        s.isoCode === savedState.isoCode ||
        s.name === savedState.name,
    );

    if (matchedState) {

      setState(matchedState);
    }
  }, [candidate, stateOptions]);

  // Auto-fill city after city options are loaded
  useEffect(() => {
    if (!candidate?.address?.city || cityOptions.length === 0) return;

    const savedCity = candidate.address.city;
    const matchedCity = cityOptions.find((c) => c.name === savedCity.name);

    if (matchedCity) {

      setCity(matchedCity);
    }
  }, [candidate, cityOptions]);

  // Auto-fill Job based on jobId (friendly ID) if ObjectId link is missing
  useEffect(() => {
    // Only proceed if we have candidate data and jobs list, but formData.job is empty
    if (!candidate?.jobId || jobs.length === 0) return;

    // Check if job is already selected correctly (has a value)
    if (formData.job) return;



    // Find job by friendly ID (jobId)
    // We check all possible ID fields to be safe
    const matchedJob = jobs.find(
      (j) =>
        j.jobId === candidate.jobId ||
        j.id === candidate.jobId ||
        j._id === candidate.jobId,
    );

    if (matchedJob) {

      // Use the MongoDB _id (or id) which is what the dropdown expects as value
      const jobObjectId = matchedJob._id || matchedJob.id;

      setFormData((prev) => ({
        ...prev,
        job: jobObjectId,
      }));
    } else {

    }
  }, [candidate, jobs, formData.job]);

  const uploadFile = async (file) => {
    if (!file) return;

    // Check file size (25MB limit)
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File size exceeds 25MB limit");
      return;
    }

    // Check file type
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PDF, DOC, DOCX, and TXT documents are allowed");
      return;
    }

    setIsUploadingResume(true);
    setUploadProgress(0);
    setErrors((prev) => ({ ...prev, resume: null }));

    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 95) return 95;
        return prev + 5;
      });
    }, 150);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("folder", "resumes");

      const result = await post(
        API_ENDPOINTS.UPLOAD.CLOUDINARY,
        uploadFormData,
        { timeout: 120000 } // 2 minutes timeout for document upload
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.responseCode === 200) {
        const resumeMetadata = {
          name: file.name,
          filename: file.name,
          originalName: file.name,
          size: file.size,
          mimetype: file.type,
          mimeType: file.type,
          url: result.data.url,
          public_id: result.data.public_id,
          isNewUpload: true,
        };

        // Small delay to show 100% completion
        setTimeout(() => {
          setFormData((prev) => ({
            ...prev,
            resume: resumeMetadata,
          }));
          setUploadProgress(0);
          toast.success("Resume uploaded successfully");
        }, 800);
      } else {
        setErrors((prev) => ({
          ...prev,
          resume: result.responseMessage || "Failed to upload resume",
        }));
        toast.error(result.responseMessage || "Failed to upload resume");
        setUploadProgress(0);
      }
    } catch (error) {
      clearInterval(progressInterval);

      setErrors((prev) => ({
        ...prev,
        resume: "An error occurred during resume upload",
      }));
      toast.error("An error occurred during resume upload");
      setUploadProgress(0);
    } finally {
      setIsUploadingResume(false);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    await uploadFile(file);
    // Reset input
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    await uploadFile(file);
  };

  const getResumeDownloadFilename = (resume) => {
    if (!resume) return "resume.pdf";

    const fallbackName =
      resume.originalName || resume.filename || resume.name || "resume";
    if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) {
      return fallbackName;
    }

    const mimeType = (resume.mimetype || resume.mimeType || "").toLowerCase();
    if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
    if (mimeType === "application/msword") return `${fallbackName}.doc`;
    if (
      mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    )
      return `${fallbackName}.docx`;
    if (mimeType === "text/plain") return `${fallbackName}.txt`;

    return `${fallbackName}.pdf`;
  };

  const parseFilenameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition) return "";

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]).replace(/(^"|"$)/g, "");
      } catch (_) {
        return utf8Match[1].replace(/(^"|"$)/g, "");
      }
    }

    const simpleMatch = contentDisposition.match(/filename=([^;]+)/i);
    return simpleMatch?.[1]
      ? simpleMatch[1].trim().replace(/(^"|"$)/g, "")
      : "";
  };

  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || "resume").trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

    const type = String(mimeType || "").toLowerCase();
    if (type === "application/pdf") return `${clean}.pdf`;
    if (type === "application/msword") return `${clean}.doc`;
    if (
      type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    )
      return `${clean}.docx`;
    if (type === "text/plain") return `${clean}.txt`;

    return `${clean}.pdf`;
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleResumeDownload = async () => {
    if (!formData.resume) return;

    const fallbackFilename = getResumeDownloadFilename(formData.resume);

    try {
      if (id) {
        const token = localStorage.getItem("auth_token");
        const response = await fetch(
          `${API_BASE_URL}/recruitment/candidates/${id}/resume`,
          {
            method: "GET",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!response.ok) {
          if (response.status === 404) {
            toast.error("Document not found", {
              icon: "⚠️",
            });
            return;
          }
          throw new Error("Failed to download resume");
        }

        const blob = await response.blob();
        const headerFilename = parseFilenameFromContentDisposition(
          response.headers.get("content-disposition"),
        );
        const resolvedFilename = ensureFilenameHasExtension(
          headerFilename || fallbackFilename,
          blob.type || formData.resume?.mimetype || formData.resume?.mimeType,
        );

        const blobUrl = window.URL.createObjectURL(blob);
        triggerBrowserDownload(blobUrl, resolvedFilename);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } else if (formData.resume?.public_id || formData.resume?.publicId) {
        const token = localStorage.getItem("auth_token");
        const publicId =
          formData.resume?.public_id || formData.resume?.publicId;

        const response = await fetch(
          `${API_BASE_URL}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(fallbackFilename)}`,
          {
            method: "GET",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!response.ok) {
          if (response.status === 404) {
            toast.error("Document not found", {
              icon: "⚠️",
            });
            return;
          }
          throw new Error("Failed to download resume");
        }

        const blob = await response.blob();
        const headerFilename = parseFilenameFromContentDisposition(
          response.headers.get("content-disposition"),
        );
        const resolvedFilename = ensureFilenameHasExtension(
          headerFilename || fallbackFilename,
          blob.type || formData.resume?.mimetype || formData.resume?.mimeType,
        );

        const blobUrl = window.URL.createObjectURL(blob);
        triggerBrowserDownload(blobUrl, resolvedFilename);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } else if (formData.resume.url) {
        try {
          const response = await fetch(formData.resume.url);
          if (!response.ok) {
            if (response.status === 404) {
              toast.error("Document not found", {
                icon: "⚠️",
              });
              return;
            }
            throw new Error("Failed to fetch resume file");
          }

          const blob = await response.blob();
          const resolvedFilename = ensureFilenameHasExtension(
            fallbackFilename,
            blob.type || formData.resume?.mimetype || formData.resume?.mimeType,
          );

          const blobUrl = window.URL.createObjectURL(blob);
          triggerBrowserDownload(blobUrl, resolvedFilename);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        } catch (directFetchError) {
          // Fallback for CORS-restricted file URLs.
          const resolvedFilename = ensureFilenameHasExtension(
            fallbackFilename,
            formData.resume?.mimetype || formData.resume?.mimeType,
          );
          triggerBrowserDownload(formData.resume.url, resolvedFilename);
        }
      }
    } catch (error) {

      toast.error(error.message || "Failed to download resume");
    }
  };

  const removeResume = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Determine if this is an existing file (either in edit mode or already saved metadata)
    const isExistingFile = !!(id || formData.resume?.publicId || (formData.resume?.url && !formData.resume?.isNewUpload));

    // Ask for confirmation if it's an existing file or if we are in edit mode
    if (isExistingFile || id) {
      const confirmationResult = window.confirm(
        "Are you sure you want to remove this document? This will delete the file from storage.",
      );

      const resolvedConfirmation =
        typeof confirmationResult?.then === "function"
          ? await confirmationResult
          : confirmationResult;

      const confirmed =
        typeof resolvedConfirmation === "object" && resolvedConfirmation !== null
          ? !!(
            resolvedConfirmation.isConfirmed ??
            resolvedConfirmation.confirmed ??
            resolvedConfirmation.value
          )
          : !!resolvedConfirmation;

      if (!confirmed) return;
    }

    setIsDeletingResume(true);
    try {
      const publicId = formData.resume?.public_id || formData.resume?.publicId;
      if (publicId) {
        await del(
          `${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${publicId}&resource_type=raw`,
        );
      }

      setFormData((prev) => ({
        ...prev,
        resume: null,
      }));
      setErrors((prev) => ({
        ...prev,
        resume: null,
      }));
      toast.success("Document removed successfully");
    } catch (error) {

      toast.error("Failed to remove document");
    } finally {
      setIsDeletingResume(false);
    }
  };

  // Define field types and limits
  const fieldConfig = {
    // Text fields: alphanumeric, spaces, comma
    firstName: { type: "text", limit: 50 },
    lastName: { type: "text", limit: 50 },
    address: { type: "text", limit: 100 },
    jobTitle: { type: "text", limit: 100 },
    department: { type: "text", limit: 100 },
    currentCompany: { type: "text", limit: 100 },
    education: { type: "text", limit: 100 },
    university: { type: "text", limit: 100 },
    notes: { type: "text", limit: 500 },
    // Number fields: numbers only
    phone: { type: "number", limit: 10 },
    experience: { type: "text", limit: 50 },
    noticePeriod: { type: "text", limit: 50 }, // Changed to text for dropdown values like "15-days", "30-days"
    graduationYear: { type: "number", limit: 4 },
    expectedSalary: { type: "number", limit: 50 },
    // Email field
    email: { type: "email", limit: 100 },
    // URL fields (no special validation, just limit)
    linkedin: { type: "url", limit: 200 },
    github: { type: "url", limit: 200 },
    portfolio: { type: "url", limit: 200 },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const config = fieldConfig[name] || { type: "text", limit: 100 };

    let processedValue = value;

    // Special handling for select dropdowns - preserve the value as-is
    if (e.target.tagName === "SELECT") {
      processedValue = value;
    } else if (config.type === "text") {
      // Special handling for address fields
      if (name === "postalCode") {
        processedValue = value
          .replace(/[^a-zA-Z0-9\s]/g, "")
          .substring(0, config.limit);
      } else if (name === "experience") {
        // Experience: allow numbers and only one + sign, but only after a number
        let val = value.replace(/[^0-9+]/g, "");
        let result = "";
        let hasPlus = false;
        for (let i = 0; i < val.length; i++) {
          if (val[i] === "+") {
            if (!hasPlus && result.length > 0) {
              result += "+";
              hasPlus = true;
            }
          } else {
            result += val[i];
          }
        }
        processedValue = result.substring(0, config.limit);
      } else {
        // Text fields: allow requested symbols
        processedValue = value
          .replace(INVALID_CHARS_REGEX, "")
          .substring(0, config.limit);
      }
    } else if (config.type === "number") {
      // Number fields: only numbers, prevent negative
      processedValue = value.replace(/\D/g, "").substring(0, config.limit);
      // Ensure it's a positive number (0 or greater)
      if (processedValue && parseInt(processedValue) < 0) {
        processedValue = "0";
      }
    } else if (config.type === "email") {
      // Email: allow valid email characters (no validation here, just allow typing)
      processedValue = value.substring(0, config.limit);
    } else if (config.type === "url") {
      // URL fields: allow all characters
      processedValue = value.substring(0, config.limit);
    } else {
      // Default: apply limit only
      processedValue = value.substring(0, config.limit);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleInputKeyDown = (e) => {
    const { name, value } = e.target;
    const config = fieldConfig[name] || { type: "text", limit: 100 };

    // Allow navigation and control keys for all fields
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)
    ) {
      return;
    }

    // Check if field is at character limit
    if (value.length >= config.limit) {
      e.preventDefault();
      return;
    }

    // Field-specific character validation
    if (config.type === "number") {
      // Number fields: only allow numbers, prevent negative sign and decimal
      if (
        e.key === "-" ||
        e.key === "." ||
        e.key === "e" ||
        e.key === "E" ||
        ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
          (e.keyCode < 96 || e.keyCode > 105))
      ) {
        e.preventDefault();
      }
    }
    // Email and URL fields: allow all characters (no keydown restriction)
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;

    // Check if we have rules for this field
    const rules = candidateValidationRules[name];
    if (rules) {
      const fieldErrors = validateField(name, value, rules);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldErrors.length > 0 ? fieldErrors[0] : "",
      }));
    }
  };

  // Handle location dropdown changes
  const handleCountryChange = (value) => {


    setCountry(value);

    // Only clear dependent fields if not auto-filling
    if (!isAutoFilling) {
      setState(null);
      setCity(null);
    }

    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        country: value,
        state: null,
        city: null,
      },
    }));

    // Clear any location-related errors
    setErrors((prev) => ({
      ...prev,
      country: null,
      state: null,
      city: null,
    }));
  };

  const handleStateChange = (value) => {
    setState(value);

    // Only clear city if not auto-filling
    if (!isAutoFilling) {
      setCity(null);
    }

    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        state: value,
        city: null,
      },
    }));

    // Clear state and city errors
    setErrors((prev) => ({
      ...prev,
      state: null,
      city: null,
    }));
  };

  const handleCityChange = (value) => {
    setCity(value);
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        city: value,
      },
    }));

    // Clear city error
    setErrors((prev) => ({
      ...prev,
      city: null,
    }));
  };

  // Handle address field changes (street address and postal code)
  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    if (name === "streetAddress") {
      // Allow requested symbols
      processedValue = value.replace(INVALID_CHARS_REGEX, "");
    } else if (name === "postalCode") {
      // Only allow alphanumeric characters and spaces
      processedValue = value.replace(/[^a-zA-Z0-9\s]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: processedValue,
      },
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Handle address field blur
  const handleAddressBlur = (e) => {
    const { name, value } = e.target;

    let error = null;
    if (name === "streetAddress") {
      if (!value || value.trim().length < 5) {
        error = "Street address must be at least 5 characters long";
      }
    } else if (name === "postalCode") {
      if (!value || value.trim().length < 3) {
        error = "Postal code must be at least 3 characters long";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Handle location dropdown blur events
  const handleLocationBlur = (field) => {
    const value = formData.address?.[field];
    setErrors((prev) => ({
      ...prev,
      [field]: !value
        ? `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
        : null,
    }));
  };

  const handleSkillAdd = (skill) => {
    if (
      skill &&
      skill.trim().length >= 2 &&
      !formData.skills.includes(skill.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, skill.trim()],
      }));
    }
    setNewSkill("");
  };

  const handleSkillRemove = (skillToRemove) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToRemove),
    }));
  };

  const handleAddEducation = () => {
    if (formData.educations.length >= 5) {
      toast.error("Maximum 5 education entries allowed");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      educations: [
        ...prev.educations,
        { education: "", university: "", graduationYear: "" },
      ],
    }));
  };

  const handleRemoveEducation = (index) => {
    if (formData.educations.length > 1) {
      setFormData((prev) => ({
        ...prev,
        educations: prev.educations.filter((_, i) => i !== index),
      }));
    } else {
      // Clear the only entry if it's the last one
      setFormData((prev) => ({
        ...prev,
        educations: [{ education: "", university: "", graduationYear: "" }],
      }));
    }
  };

  const handleEducationChange = (index, field, value) => {
    let processedValue = value;

    // Auto-correct graduation year if > current year + 4
    if (field === "graduationYear" && value.length === 4) {
      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      const maxYear = currentYear + 4;

      if (year > maxYear) {
        processedValue = maxYear.toString();
        toast.dismiss("year-limit");
        toast(`Year limited to ${maxYear} (max 4 years future)`, {
          icon: "ℹ️",
          id: "year-limit",
          duration: 3000,
        });
      }
    }

    const updatedEducations = [...formData.educations];
    updatedEducations[index][field] = processedValue;

    // Also update the primary fields for backward compatibility if it's the first entry
    if (index === 0) {
      setFormData((prev) => ({
        ...prev,
        educations: updatedEducations,
        [field]: processedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        educations: updatedEducations,
      }));
    }

    // Clear error for that specific index
    const errorKey = `${field}_${index}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: "",
      }));
    }
  };

  const validateEducationField = (field, value) => {
    if (field === "education" && (!value || !value.trim())) {
      return "Degree/Education is required";
    }
    if (field === "university" && (!value || !value.trim())) {
      return "University/School is required";
    }
    if (field === "graduationYear") {
      if (!value) return "Graduation year is required";
      // Allow 4 digits. Logic for future years is handled below.
      if (!/^\d{4}$/.test(value.toString()))
        return "Enter a valid 4-digit year";

      const year = parseInt(value);
      const currentYear = new Date().getFullYear();
      const maxYear = currentYear + 4;

      if (year < 1900) return "Year must be after 1900";
      if (year > maxYear) return `Year cannot be later than ${maxYear}`;
    }
    return "";
  };

  const handleEducationBlur = (index, field, value) => {
    const error = validateEducationField(field, value);
    setErrors((prev) => ({
      ...prev,
      [`${field}_${index}`]: error,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate main record fields using standard rules
    Object.keys(candidateValidationRules).forEach((name) => {
      let value;

      // Handle nested address fields
      if (
        ["country", "state", "city", "streetAddress", "postalCode"].includes(
          name,
        )
      ) {
        value = formData.address?.[name];

        // For location dropdowns (objects), just check if they exist IF they are required
        if (["country", "state", "city"].includes(name)) {
          const rules = candidateValidationRules[name];
          if (rules?.required && (!value || !value.id)) {
            newErrors[name] =
              `${name.charAt(0).toUpperCase() + name.slice(1)} is required`;
            isValid = false;
          }
          return; // Skip the standard validateField for objects
        }
      } else {
        value = formData[name];
      }

      const rules = candidateValidationRules[name];
      const fieldErrors = validateField(name, value, rules);
      if (fieldErrors.length > 0) {
        newErrors[name] = fieldErrors[0];
        isValid = false;
      }
    });

    // Validate educations array
    formData.educations.forEach((edu, index) => {
      const eduError = validateEducationField("education", edu.education);
      if (eduError) {
        newErrors[`education_${index}`] = eduError;
        isValid = false;
      }

      const uniError = validateEducationField("university", edu.university);
      if (uniError) {
        newErrors[`university_${index}`] = uniError;
        isValid = false;
      }

      const gradError = validateEducationField(
        "graduationYear",
        edu.graduationYear,
      );
      if (gradError) {
        newErrors[`graduationYear_${index}`] = gradError;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors before submitting.");
      return;
    }

    setSaving(true);
    const toastId = toast.loading(`${id ? "Updating" : "Adding"} candidate...`);

    try {
      // Always create FormData for consistency
      const candidateData = new FormData();

      // Append basic fields
      candidateData.append("firstName", formData.firstName);
      candidateData.append("lastName", formData.lastName);
      candidateData.append("email", formData.email);
      candidateData.append("phone", formData.phone);

      const addressPayload = {
        streetAddress: formData.address.streetAddress || "",
        postalCode: formData.address.postalCode || "",
        country: formData.address.country
          ? {
            id: formData.address.country.id,
            name: formData.address.country.name,
            isoCode: formData.address.country.isoCode,
          }
          : null,
        state: formData.address.state
          ? {
            id: formData.address.state.id,
            name: formData.address.state.name,
            isoCode: formData.address.state.isoCode,
          }
          : null,
        city: formData.address.city
          ? {
            id: formData.address.city.id,
            name: formData.address.city.name,
          }
          : null,
      };

      candidateData.append("address", JSON.stringify(addressPayload));
      candidateData.append("status", formData.status);
      candidateData.append("stage", formData.stage);
      candidateData.append("job", formData.job || "");
      candidateData.append("jobTitle", formData.jobTitle);
      candidateData.append("department", formData.department);
      candidateData.append(
        "experience",
        JSON.stringify({
          name: formData.experience,
          ref: formData.experienceRef || null,
        }),
      );
      candidateData.append("currentCompany", formData.currentCompany);
      candidateData.append("expectedSalary", formData.expectedSalary);
      candidateData.append("noticePeriod", formData.noticePeriod);
      candidateData.append("education", formData.education);
      candidateData.append("university", formData.university);
      candidateData.append("graduationYear", formData.graduationYear);
      candidateData.append("educations", JSON.stringify(formData.educations));
      candidateData.append("linkedin", formData.linkedin);
      candidateData.append("github", formData.github);
      candidateData.append("portfolio", formData.portfolio);
      candidateData.append("source", formData.source);
      candidateData.append("notes", formData.notes);
      candidateData.append("availability", formData.availability);
      candidateData.append("rating", formData.rating || 0);

      // Append skills
      const flatSkills = [];
      const flattenArray = (arr) => {
        arr.forEach((item) => {
          if (Array.isArray(item)) flattenArray(item);
          else if (typeof item === "string" && item.trim())
            flatSkills.push(item.trim());
        });
      };
      flattenArray(formData.skills);
      flatSkills.forEach((skill) => candidateData.append("skills", skill));

      // Always append resume field (send null if removed) so backend knows to clear it
      candidateData.append("resume", JSON.stringify(formData.resume || null));

      let result;
      if (id) {
        result = await candidateService.updateCandidate(id, candidateData);
      } else {
        result = await candidateService.addCandidate(candidateData);
      }

      const isSuccess =
        result &&
        (result.responseCode === 201 ||
          result.responseCode === 200 ||
          result.success === true ||
          (result.data && result.data.candidateId));

      if (isSuccess) {
        toast.success(`Candidate ${id ? "updated" : "added"} successfully!`, {
          id: toastId,
        });
        setTimeout(() => navigate(location.state?.from || "/hrms/back-office?tab=candidateProfiles"), 1000);
      } else {
        const errorMessage =
          result?.responseMessage ||
          result?.message ||
          `Failed to ${id ? "update" : "create"} candidate`;
        toast.error(errorMessage, { id: toastId });
      }
    } catch (error) {

      toast.error(
        error.message || `Error ${id ? "updating" : "creating"} candidate.`,
        { id: toastId },
      );
    } finally {
      setSaving(false);
    }
  };

  // Debug logs for selected values


  // Additional debugging for country structure
  if (country) {
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          className="bg-transparent hover:bg-transparent text-brand focus:ring-0 focus:ring-offset-0"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate(-1)}
        ></Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? "Edit Candidate" : "Add New Candidate"}
          </h1>
          <p className="text-gray-600">
            {id
              ? "Update candidate information"
              : "Create a new candidate profile"}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <User className="h-5 w-5 mr-2" />
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                maxLength={50}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.firstName ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="Enter first name"
                required
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.firstName} />
                <div className="flex items-center space-x-2 ml-auto">
                  <p className="text-xs text-gray-500">
                    {formData.firstName.length}/50 characters
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                maxLength={50}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.lastName ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="Enter last name"
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.lastName} />
                <div className="flex items-center space-x-2 ml-auto">
                  <p className="text-xs text-gray-500">
                    {formData.lastName.length}/50 characters
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                maxLength={100}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.email ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="Enter email address"
                required
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.email} />
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.email.length}/100 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                maxLength={10}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.phone ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="1234567890"
                required
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.phone} />
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.phone.length}/10 digits
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stage
              </label>
              <select
                name="stage"
                value={formData.stage}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                {stageOptions.map((stage) => (
                  <option key={stage} value={stage} className="capitalize">
                    {stage.charAt(0).toUpperCase() + stage.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Location Information */}
          <div className="mt-8">
            <h4 className="text-md font-medium text-gray-900 mb-4">
              Address Information
            </h4>

            {/* Country/State/City and Address in two-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Country Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <LocationSelect
                  options={countryOptions}
                  value={country}
                  onChange={handleCountryChange}
                  onBlur={() => handleLocationBlur("country")}
                  placeholder="Select country"
                  error={!!errors.country}
                />
                {errors.country && (
                  <div className="flex items-center mt-1 text-red-600 text-sm">
                    <ErrorMessage error={errors.country} />
                  </div>
                )}
              </div>

              {/* State Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <LocationSelect
                  options={stateOptions}
                  value={state}
                  onChange={handleStateChange}
                  onBlur={() => handleLocationBlur("state")}
                  placeholder="Select state"
                  disabled={!country}
                  error={!!errors.state}
                />
                {errors.state && (
                  <div className="flex items-center mt-1 text-red-600 text-sm">
                    <ErrorMessage error={errors.state} />
                  </div>
                )}
              </div>

              {/* City Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <LocationSelect
                  options={cityOptions}
                  value={city}
                  onChange={handleCityChange}
                  onBlur={() => handleLocationBlur("city")}
                  placeholder="Select city"
                  disabled={!state}
                  error={!!errors.city}
                />
                {errors.city && (
                  <div className="flex items-center mt-1 text-red-600 text-sm">
                    <ErrorMessage error={errors.city} />
                  </div>
                )}
              </div>

              {/* Address Input */}
              {/* Street Address and Postal Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    name="streetAddress"
                    value={formData.address.streetAddress}
                    onChange={handleAddressChange}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleAddressBlur}
                    maxLength={100}
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.streetAddress
                      ? "border-red-300"
                      : "border-gray-300"
                      }`}
                    placeholder="e.g., 123 Main Street, Apt 4B"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage error={errors.streetAddress} />
                    <p className="text-xs text-gray-500 ml-auto">
                      {formData.address.streetAddress.length}/100 characters
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.address.postalCode}
                    onChange={handleAddressChange}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleAddressBlur}
                    maxLength={20}
                    className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.postalCode ? "border-red-300" : "border-gray-300"
                      }`}
                    placeholder="e.g., 110001"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <ErrorMessage error={errors.postalCode} />
                    <p className="text-xs text-gray-500 ml-auto">
                      {formData.address.postalCode.length}/20 characters
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Briefcase className="h-5 w-5 mr-2" />
            Professional Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Position <span className="text-red-500">*</span>
              </label>
              <select
                name="job"
                value={formData.job}
                onChange={(e) => {
                  handleInputChange(e);
                  // Interactive sync: when job changes, update department and title immediately
                  const selectedJobId = e.target.value;
                  const selectedJob = jobs.find(
                    (j) => (j._id || j.id) === selectedJobId,
                  );
                  if (selectedJob) {
                    setFormData((prev) => {
                      const updates = {};
                      if (selectedJob.department) {
                        const matchingDept =
                          departments.find(
                            (d) =>
                              d.trim().toLowerCase() ===
                              selectedJob.department.trim().toLowerCase(),
                          ) || selectedJob.department;
                        updates.department = matchingDept;
                      }
                      if (selectedJob.title) {
                        updates.jobTitle = selectedJob.title;
                      }
                      return { ...prev, ...updates };
                    });
                  }
                }}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.job ? "border-red-300" : "border-gray-300"
                  }`}
                required
              >
                <option value="">Select Job Position</option>
                {jobs.map((job) => (
                  <option key={job.id || job._id} value={job.id || job._id}>
                    {job.title} - {job.department} ({job.location}){" "}
                    {job.status !== "active" && job.status !== "published"
                      ? ` [${job.status}]`
                      : ""}
                  </option>
                ))}
              </select>
              <ErrorMessage error={errors.job} />
              <p className="text-xs text-gray-500 mt-1">
                Select a job position to link this candidate to a specific
                opening
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Title (Optional)
              </label>
              <input
                type="text"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                maxLength={100}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.jobTitle ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="Enter job title (optional)"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.jobTitle && (
                  <p className="text-sm text-red-600">{errors.jobTitle}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.jobTitle.length}/100 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={loadingDepartments}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.department ? "border-red-300" : "border-gray-300"
                  } ${loadingDepartments ? "bg-gray-100 cursor-not-allowed" : ""}`}
                required
              >
                <option value="">
                  {loadingDepartments
                    ? "Loading departments..."
                    : "Select Department"}
                </option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
              <ErrorMessage error={errors.department} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience Level <span className="text-red-500">*</span>
              </label>
              <select
                name="experience"
                value={formData.experience}
                onChange={(e) => {
                  handleInputChange(e);

                  // Try to find a matching experience level ref if possible
                  const val = e.target.value;
                  const selectedExp = experienceLevels.find(
                    (exp) => exp.name === val,
                  );
                  if (selectedExp) {
                    setFormData((prev) => ({
                      ...prev,
                      experienceRef: selectedExp._id || selectedExp.id,
                    }));
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      experienceRef: "",
                    }));
                  }
                }}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.experience ? "border-red-300" : "border-gray-300"}`}
                required
              >
                <option value="">Select Experience Level</option>
                {experienceLevels.map((exp) => (
                  <option key={exp._id || exp.id} value={exp.name}>
                    {exp.name}
                  </option>
                ))}
              </select>
              <ErrorMessage error={errors.experience} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Company
              </label>
              <input
                type="text"
                name="currentCompany"
                value={formData.currentCompany}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                maxLength={100}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.currentCompany ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="Enter current company"
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.currentCompany} />
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.currentCompany.length}/100 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Salary
              </label>
              <input
                type="text"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                maxLength={8}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.expectedSalary ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="e.g., 800000 or 800000-1000000"
              />
              <div className="flex justify-between items-center mt-1">
                <ErrorMessage error={errors.expectedSalary} />
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.expectedSalary.length}/8 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notice Period
              </label>
              <select
                name="noticePeriod"
                value={formData.noticePeriod}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.noticePeriod ? "border-red-300" : "border-gray-300"
                  }`}
              >
                <option value="">Select Notice Period</option>
                <option value="immediate">Immediate</option>
                <option value="15-days">15 days</option>
                <option value="30-days">30 days</option>
                <option value="45-days">45 days</option>
                <option value="60-days">60 days</option>
                <option value="90-days">90 days</option>
              </select>
              <ErrorMessage error={errors.noticePeriod} />
            </div>
          </div>
        </div>

        {/* Education */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <GraduationCap className="h-5 w-5 mr-2" />
              Education
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleAddEducation}
            >
              Add Education
            </Button>
          </div>

          <div className="space-y-8">
            {formData.educations.map((edu, index) => (
              <div
                key={index}
                className="relative border-b border-gray-100 pb-8 last:border-0 last:pb-0"
              >
                {formData.educations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEducation(index)}
                    className="absolute -top-1 -right-1 p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Degree / Education <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={edu.education}
                      onChange={(e) =>
                        handleEducationChange(
                          index,
                          "education",
                          e.target.value,
                        )
                      }
                      maxLength={100}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors[`education_${index}`] ? "border-red-300" : "border-gray-300"}`}
                      placeholder="e.g., B.Tech Computer Science"
                      required
                      onBlur={(e) =>
                        handleEducationBlur(index, "education", e.target.value)
                      }
                    />
                    <div className="flex justify-between items-center mt-1">
                      <ErrorMessage error={errors[`education_${index}`]} />
                      <p className="text-xs text-gray-500 ml-auto">
                        {edu.education.length}/100 characters
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      University / School{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={edu.university}
                      onChange={(e) =>
                        handleEducationChange(
                          index,
                          "university",
                          e.target.value,
                        )
                      }
                      maxLength={100}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors[`university_${index}`] ? "border-red-300" : "border-gray-300"}`}
                      placeholder="Enter university name"
                      required
                      onBlur={(e) =>
                        handleEducationBlur(index, "university", e.target.value)
                      }
                    />
                    <div className="flex justify-between items-center mt-1">
                      <ErrorMessage error={errors[`university_${index}`]} />
                      <p className="text-xs text-gray-500 ml-auto">
                        {edu.university.length}/100 characters
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Graduation Year <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={edu.graduationYear}
                      onChange={(e) =>
                        handleEducationChange(
                          index,
                          "graduationYear",
                          e.target.value.replace(/\D/g, "").substring(0, 4),
                        )
                      }
                      maxLength={4}
                      className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors[`graduationYear_${index}`] ? "border-red-300" : "border-gray-300"}`}
                      placeholder="e.g., 2020"
                      required
                      onBlur={(e) =>
                        handleEducationBlur(
                          index,
                          "graduationYear",
                          e.target.value,
                        )
                      }
                    />
                    <ErrorMessage error={errors[`graduationYear_${index}`]} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skills */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Skills</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Skills
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => {
                    // Text fields: allow requested symbols
                    const processedValue = e.target.value
                      .replace(INVALID_CHARS_REGEX, "")
                      .substring(0, 50);
                    setNewSkill(processedValue);
                  }}
                  onKeyPress={(e) =>
                    e.key === "Enter" &&
                    (e.preventDefault(), handleSkillAdd(newSkill))
                  }
                  onKeyDown={(e) => {
                    // Allow navigation and control keys
                    if (
                      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(
                        e.keyCode,
                      ) !== -1 ||
                      (e.keyCode === 65 && e.ctrlKey === true) ||
                      (e.keyCode === 67 && e.ctrlKey === true) ||
                      (e.keyCode === 86 && e.ctrlKey === true) ||
                      (e.keyCode === 88 && e.ctrlKey === true)
                    ) {
                      return;
                    }

                    // Check if field is at character limit (50 characters)
                    if (newSkill.length >= 50) {
                      e.preventDefault();
                      return;
                    }
                  }}
                  maxLength={50}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  placeholder="Enter skill and press Enter"
                />
                <Button
                  type="button"
                  className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
                  onClick={() => handleSkillAdd(newSkill)}
                >
                  Add
                </Button>
              </div>
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-gray-500 ml-auto">
                  {newSkill.length}/50 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Common Skills
                {loadingSkills && <span className="ml-2 text-gray-400 font-normal text-xs">Loading...</span>}
              </label>
              <div className="flex flex-wrap gap-2">
                {commonSkills.map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => handleSkillAdd(skill)}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 capitalize"
                  >
                    {skill}
                  </button>
                ))}
              </div>
            </div>

            <div>
              {formData.skills.length > 0 && <label className="block text-sm font-medium text-gray-700 mb-2">
                Selected Skills
              </label>}
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill) => (
                  <span
                    key={skill}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm capitalize"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => handleSkillRemove(skill)}
                      className="ml-2 text-[#0F2942] hover:text-blue-800 capitalize"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Social Links & Additional Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Additional Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                LinkedIn Profile
              </label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                maxLength={200}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.linkedin ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="https://linkedin.com/in/username"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.linkedin && (
                  <p className="text-sm text-red-600">{errors.linkedin}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.linkedin.length}/200 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GitHub Profile
              </label>
              <input
                type="url"
                name="github"
                value={formData.github}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                maxLength={200}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.github ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="https://github.com/username"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.github && (
                  <p className="text-sm text-red-600">{errors.github}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.github.length}/200 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Portfolio Website
              </label>
              <input
                type="url"
                name="portfolio"
                value={formData.portfolio}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                maxLength={200}
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.portfolio ? "border-red-300" : "border-gray-300"
                  }`}
                placeholder="https://yourportfolio.com"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.portfolio && (
                  <p className="text-sm text-red-600">{errors.portfolio}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.portfolio.length}/200 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source
              </label>
              <select
                name="source"
                value={formData.source}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                {sourceOptions.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Availability
              </label>
              <select
                name="availability"
                value={formData.availability}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="Available">Available</option>
                <option value="Not Available">Not Available</option>
                <option value="Hired">Hired</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // Only allow alphanumeric, spaces, and commas
                if (
                  !/^[a-zA-Z0-9\s,]$/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onBlur={handleInputBlur}
              maxLength={500}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${errors.notes ? "border-red-300" : "border-gray-300"
                }`}
              placeholder="Additional notes about the candidate..."
            />
            <div className="flex justify-between items-center mt-1">
              {errors.notes && (
                <p className="text-sm text-red-600">{errors.notes}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.notes.length}/500 characters
              </p>
            </div>
          </div>
        </div>

        {/* Resume Upload */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Resume Upload
          </h2>

          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 relative overflow-hidden ${errors.resume
              ? "border-red-300 bg-red-50"
              : formData.resume
                ? "border-green-300 bg-green-50"
                : isDragOver
                  ? "border-brand bg-brand/5 scale-[1.02]"
                  : "border-gray-200 hover:border-brand/50 bg-gray-50/30"
              }`}
          >
            {isUploadingResume || isDeletingResume ? (
              <div className="py-4 px-4 w-full">
                <div className="flex flex-col items-center max-w-sm mx-auto">
                  <div className="relative mb-6">
                    <div
                      className={`absolute inset-0 ${isDeletingResume ? "bg-red-500/10" : "bg-brand/10"} rounded-full animate-ping scale-150 opacity-20`}
                    ></div>
                    <div className="relative bg-white rounded-full p-3 shadow-sm border border-gray-100">
                      <Loader2
                        className={`h-10 w-10 ${isDeletingResume ? "text-red-500" : "text-brand"} animate-spin`}
                      />
                    </div>
                    {!isDeletingResume && (
                      <div className="absolute -top-1 -right-1 bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                        {uploadProgress}%
                      </div>
                    )}
                  </div>

                  {isUploadingResume && (
                    <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4 overflow-hidden border border-gray-200 p-0.5">
                      <div
                        className="bg-brand h-full rounded-full transition-all duration-500 ease-out relative"
                        style={{ width: `${uploadProgress}%` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.5s_infinite]"></div>
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <p
                      className={`text-sm font-bold ${isDeletingResume ? "text-red-600" : "text-gray-800"} tracking-tight`}
                    >
                      {isDeletingResume
                        ? "Removing Document..."
                        : uploadProgress < 100
                          ? "Uploading Professional Resume..."
                          : "Finalizing Upload..."}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 font-medium">
                      {isDeletingResume
                        ? "Syncing with Cloud Storage"
                        : "Securely storing your information on Cloud"}
                    </p>
                  </div>
                </div>
              </div>
            ) : formData.resume ? (
              <div className="space-y-4 py-2">
                <div className="relative inline-flex">
                  <div className="absolute inset-0 bg-green-400/20 rounded-full animate-pulse scale-125"></div>
                  <div className="relative inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full border-2 border-green-200">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 break-all px-6 leading-tight">
                    {formData.resume.name}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-1.5">
                    <p className="text-[11px] text-gray-500 font-medium">
                      {(formData.resume.size / 1024 / 1024).toFixed(2)} MB •
                      Ready to save
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {formData.resume.url && (
                    <button
                      type="button"
                      onClick={handleResumeDownload}
                      className="inline-flex items-center px-4 py-1.5 text-[#0F2942] hover:text-white hover:bg-blue-500 border border-blue-100 hover:border-blue-500 rounded-full text-xs font-bold transition-all duration-200 group"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      Download Document
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={removeResume}
                    className="inline-flex items-center px-4 py-1.5 text-red-600 hover:text-white hover:bg-red-500 border border-red-100 hover:border-red-500 rounded-full text-xs font-bold transition-all duration-200 group"
                  >
                    <X className="h-3.5 w-3.5 mr-1.5 transition-transform group-hover:rotate-90" />
                    Remove & Change File
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="space-y-5 py-6 group cursor-pointer"
                onClick={() =>
                  document.getElementById("resumeFileInput").click()
                }
              >
                <div className="mx-auto w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center border-2 border-gray-100 shadow-sm group-hover:border-brand/30 group-hover:bg-brand/5 transition-all duration-500 group-hover:rotate-3">
                  <Upload className="h-10 w-10 text-gray-400 group-hover:text-brand transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-base text-gray-800 font-bold tracking-tight">
                    Drop resume here or{" "}
                    <span className="text-brand underline decoration-brand/30 underline-offset-4">
                      browse files
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-2 font-medium">
                    Supported: PDF, DOC, DOCX up to 25MB
                  </p>
                </div>
                <input
                  id="resumeFileInput"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleResumeUpload}
                  className="hidden"
                />
              </div>
            )}
            {errors.resume && (
              <p className="text-sm text-red-600 mt-4 font-medium">
                {errors.resume}
              </p>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            onClick={() => navigate(location.state?.from || "/hrms/back-office?tab=candidateProfiles")}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="px-6 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
            icon={<Save className="h-4 w-4" />}
            disabled={saving || isUploadingResume || isDeletingResume}
          >
            {saving ? "Saving..." : id ? "Update Candidate" : "Save Candidate"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddCandidate;
